import React, {
  useEffect,
} from "react";

import {
  useLocation,
} from "react-router-dom";

import {
  analyticsAllowed,
  trackAnalyticsEvent,
} from "../analytics/siteAnalytics";

const HEATMAP_CLICK_LIMIT =
  200;

const HEATMAP_COUNT_KEY =
  "gf_heatmap_click_count";

const getHeatmapClickCount =
  () => {
    try {
      return Number(
        sessionStorage.getItem(
          HEATMAP_COUNT_KEY
        ) || 0
      );
    } catch {
      return 0;
    }
  };

const setHeatmapClickCount = (
  value
) => {
  try {
    sessionStorage.setItem(
      HEATMAP_COUNT_KEY,
      String(value)
    );
  } catch {
    /*
      The tracker can still
      work without the local
      rate-limit counter.
    */
  }
};

const getSafeTarget = (
  originalTarget
) => {
  if (
    !(
      originalTarget
      instanceof Element
    )
  ) {
    return null;
  }

  return (
    originalTarget.closest(
      [
        "a",
        "button",
        "[role='button']",
        "[data-analytics]",
        "input",
        "select",
        "textarea",
        "[contenteditable='true']",
      ].join(", ")
    ) ||
    originalTarget
  );
};

const getSafeTargetLabel = (
  target
) => {
  if (!target) return "";

  const isFormField =
    Boolean(
      target.closest(
        [
          "input",
          "select",
          "textarea",
          "[contenteditable='true']",
        ].join(", ")
      )
    );

  const explicitLabel =
    target.getAttribute(
      "data-analytics-label"
    ) ||
    target.getAttribute(
      "data-analytics"
    ) ||
    target.getAttribute(
      "aria-label"
    ) ||
    target.getAttribute(
      "title"
    ) ||
    "";

  if (explicitLabel) {
    return explicitLabel
      .trim()
      .replace(
        /\s+/g,
        " "
      )
      .slice(
        0,
        120
      );
  }

  /*
    Do not use textContent or
    value for form fields.
  */
  if (isFormField) {
    return (
      target.getAttribute(
        "name"
      ) ||
      target.getAttribute(
        "id"
      ) ||
      target.tagName
        .toLowerCase()
    )
      .trim()
      .slice(
        0,
        120
      );
  }

  return (
    target.textContent ||
    target.getAttribute(
      "href"
    ) ||
    target.tagName
      .toLowerCase()
  )
    .trim()
    .replace(
      /\s+/g,
      " "
    )
    .slice(
      0,
      120
    );
};

const getElementType = (
  target
) => {
  if (!target) {
    return "unknown";
  }

  const tag =
    target.tagName
      ?.toLowerCase() ||
    "unknown";

  const role =
    target.getAttribute?.(
      "role"
    ) || "";

  return role
    ? `${tag}:${role}`
    : tag;
};

export default function HeatmapClickTracker() {
  const location =
    useLocation();

  useEffect(() => {
    const params =
      new URLSearchParams(
        window.location.search
      );

    /*
      The preview iframe must
      never record itself.
    */
    if (
      params.get(
        "analyticsPreview"
      ) === "1"
    ) {
      return undefined;
    }

    const handleClick = (
      event
    ) => {
      if (
        !analyticsAllowed(
          location.pathname
        )
      ) {
        return;
      }

      const recordedCount =
        getHeatmapClickCount();

      if (
        recordedCount >=
        HEATMAP_CLICK_LIMIT
      ) {
        return;
      }

      const documentElement =
        document.documentElement;

      const body =
        document.body;

      const documentWidth =
        Math.max(
          window.innerWidth,

          documentElement
            ?.scrollWidth ||
            0,

          body?.scrollWidth ||
            0
        );

      const documentHeight =
        Math.max(
          window.innerHeight,

          documentElement
            ?.scrollHeight ||
            0,

          body?.scrollHeight ||
            0
        );

      if (
        !documentWidth ||
        !documentHeight
      ) {
        return;
      }

      const documentX =
        event.clientX +
        window.scrollX;

      const documentY =
        event.clientY +
        window.scrollY;

      const target =
        getSafeTarget(
          event.target
        );

      setHeatmapClickCount(
        recordedCount + 1
      );

      trackAnalyticsEvent(
        "heatmap_click",
        {
          pagePath:
            `${location.pathname}${location.search}`,

          documentXPercent:
            (
              documentX /
              documentWidth
            ) * 100,

          documentYPercent:
            (
              documentY /
              documentHeight
            ) * 100,

          viewportXPercent:
            window.innerWidth
              ? (
                  event.clientX /
                  window.innerWidth
                ) * 100
              : 0,

          viewportYPercent:
            window.innerHeight
              ? (
                  event.clientY /
                  window.innerHeight
                ) * 100
              : 0,

          documentWidth,
          documentHeight,

          viewportWidth:
            window.innerWidth,

          viewportHeight:
            window.innerHeight,

          targetLabel:
            getSafeTargetLabel(
              target
            ),

          analyticsId:
            target?.getAttribute(
              "data-analytics"
            ) ||
            target?.id ||
            "",

          elementType:
            getElementType(
              target
            ),
        }
      );
    };

    document.addEventListener(
      "click",
      handleClick,
      true
    );

    return () => {
      document.removeEventListener(
        "click",
        handleClick,
        true
      );
    };
  }, [
    location.pathname,
    location.search,
  ]);

  return null;
}